import { Link } from 'react-router-dom';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Facebook, Instagram, Linkedin, Mail, Phone, MapPin, Heart } from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PrivacyPolicyModal, TermsOfUseModal } from '../LegalModals';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success('Successfully subscribed to our newsletter!');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="bg-[hsl(346,85%,25%)] text-white" data-testid="footer">
      {/* Fundraising Banner */}
      <div className="bg-[hsl(346,100%,65%)] text-white py-4">
        <div className="container-main text-center">
          <p className="font-bold text-lg">
            Help us raise $60,000 for Cayman's first Neurodivergent Community Centre!
          </p>
          <a 
            href="https://donate.autismacceptance.ky/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block mt-2"
          >
            <Button variant="secondary" className="bg-white text-[hsl(346,100%,50%)] hover:bg-white/90">
              <Heart className="h-4 w-4 mr-2" />
              Donate Now
            </Button>
          </a>
        </div>
      </div>

      <div className="container-main py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* About */}
          <div>
            <img 
              src="/logo.png" 
              alt="Autism Acceptance" 
              className="h-14 mb-4 object-contain brightness-0 invert" 
              style={{ maxWidth: '160px' }}
            />
            <p className="text-white/80 text-sm mb-4">
              Empowering autistic individuals in the Cayman Islands by fostering acceptance, 
              promoting inclusivity, and providing essential resources.
            </p>
            <p className="text-white/60 text-xs">
              Registered Non-Profit Organisation<br />
              Cayman Islands NP-693
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-white/80 hover:text-white">About Us</Link></li>
              <li><Link to="/learn" className="text-white/80 hover:text-white">Learn About Autism</Link></li>
              <li><Link to="/directory" className="text-white/80 hover:text-white">Service Directory</Link></li>
              <li><Link to="/resources" className="text-white/80 hover:text-white">Resources</Link></li>
              <li><Link to="/events" className="text-white/80 hover:text-white">Events</Link></li>
              <li><Link to="/volunteer" className="text-white/80 hover:text-white">Volunteer</Link></li>
              <li><Link to="/faqs" className="text-white/80 hover:text-white">FAQs</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-lg mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-[hsl(346,100%,65%)]" />
                <a href="mailto:hello@autismacceptance.ky" className="text-white/80 hover:text-white">
                  hello@autismacceptance.ky
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-[hsl(346,100%,65%)]" />
                <a href="mailto:admin@autismacceptance.ky" className="text-white/80 hover:text-white">
                  admin@autismacceptance.ky
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-[hsl(346,100%,65%)] mt-1" />
                <span className="text-white/80">Grand Cayman, Cayman Islands</span>
              </li>
            </ul>
            
            {/* Social Links */}
            <div className="flex gap-3 mt-4">
              <a 
                href="https://www.facebook.com/profile.php?id=61573091817988" 
                target="_blank" 
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"
                aria-label="Facebook"
                data-testid="social-facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a 
                href="https://www.instagram.com/autism_acceptance/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"
                aria-label="Instagram"
                data-testid="social-instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a 
                href="https://www.linkedin.com/company/autism-acceptance/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"
                aria-label="LinkedIn"
                data-testid="social-linkedin"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-bold text-lg mb-4">Stay Connected</h4>
            <p className="text-white/80 text-sm mb-4">
              Subscribe to our monthly newsletter for updates, resources, and community news.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-2">
              <Input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                data-testid="newsletter-email"
              />
              <Button 
                type="submit" 
                className="w-full bg-[hsl(346,100%,65%)] hover:bg-[hsl(346,100%,55%)] text-white"
                disabled={subscribing}
                data-testid="newsletter-submit"
              >
                {subscribing ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
            
            {/* Support Groups Link */}
            <div className="mt-4">
              <a 
                href="https://www.facebook.com/groups/416389465908329" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[hsl(346,100%,70%)] hover:text-[hsl(346,100%,80%)] text-sm underline"
              >
                Join Caymums & Dads Support Group
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 mt-12 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/60 text-sm text-center md:text-left">
            Copyright 2026 Autism Acceptance. All rights reserved. Materials may be used for distribution purposes.
          </p>
          <div className="flex gap-4 text-sm">
            <PrivacyPolicyModal />
            <TermsOfUseModal />
          </div>
        </div>
      </div>
    </footer>
  );
};
